import { LoadingButton } from '@/components/common/LoadingButton';
import { Product } from '@/domain/products/types';
import { SuccessToast } from '@/features/cart/components';
import { addToCart, useCartStore } from '@/features/cart/store/cart';
import pdf from '@/services/pdf';
import { Tooltip } from '@andritzot/metris-web-ui/data-display/tooltip';
import { Button } from '@andritzot/metris-web-ui/inputs/button';
import { AddShoppingCartOutlined, FileDownloadOutlined } from '@mui/icons-material';
import { usePiwikPro } from '@piwikpro/next-piwik-pro';
import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';

interface ProductPanelFooterProps {
  detailedProduct: Product;
  isLoggedIn: boolean;
}

export const ProductPanelFooter = ({ detailedProduct, isLoggedIn }: ProductPanelFooterProps) => {
  const { eCommerce } = usePiwikPro();
  const cartProducts = useCartStore((s) => s.cartProducts);
  const isAlreadyInCart = cartProducts.some((product) => product.idProduct === detailedProduct.idProduct);
  const [isLoadingPDF, setIsLoadingPDF] = useState(false);

  const handleAddToCart = () => {
    if (!detailedProduct) return;
    eCommerce.ecommerceAddToCart([
      {
        sku: `${detailedProduct.idProduct}`,
        name: detailedProduct.product,
        category: ['group' in detailedProduct ? detailedProduct.group.productGroup : ''],
        price: 0,
        quantity: 1,
      },
    ]);
    addToCart({
      idProduct: detailedProduct.idProduct,
      image2: detailedProduct.image2,
      product: detailedProduct.product,
      group: 'group' in detailedProduct ? detailedProduct.group.productGroup : '',
      documentation: detailedProduct.documentation,
    });
    toast.success(<SuccessToast title={detailedProduct.product} />, { autoClose: 5000 });
  };

  const exportPDF = useCallback(async () => {
    if (!detailedProduct) return;
    try {
      setIsLoadingPDF(true);
      await pdf.downloadPDFAsync(`/pdf/from-repository/${detailedProduct.documentation}`, detailedProduct.product);
      toast.success('Brochure downloaded successfully');
    } finally {
      setIsLoadingPDF(false);
    }
  }, [detailedProduct]);

  return (
    <>
      {isLoggedIn && detailedProduct?.documentation && (
        <LoadingButton loading={isLoadingPDF} icon={<FileDownloadOutlined />} variant="outlined" onClick={exportPDF}>
          Export Brochure
        </LoadingButton>
      )}
      {!isLoggedIn && !!detailedProduct.idProduct && (
        <Tooltip title={isAlreadyInCart ? 'Product already in cart' : 'Add product to cart'} placement="top">
          <Button
            variant="contained"
            disabled={isAlreadyInCart}
            startIcon={<AddShoppingCartOutlined />}
            onClick={handleAddToCart}
            sx={{ backgroundColor: 'primary.dark' }}
          >
            Add to Cart
          </Button>
        </Tooltip>
      )}
    </>
  );
};
